import React, { useState, useEffect, useRef } from "react";
import { Drawer } from "antd";
import type { DrawerProps } from "antd";
import { BStarIcon, SendIcon } from "../shared/icons";
import TravelDataRenderer from "./TravelDataRenderer";
import WelcomeMessage from "./WelcomeMessage";
import AIResponseContent from "./AIResponseContent";
import { TravelData, Message } from "../shared/type";
import { useStreamTravelData } from "./useStreamTravelData";

const DrawerBar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState<DrawerProps["size"]>();
  const [destination, setDestination] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "assistant",
      content: <WelcomeMessage />,
      timestamp: new Date(),
    },
  ]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const showDefaultDrawer = () => {
    setSize("default");
    setOpen(true);
  };
  const showLargeDrawer = () => {
    setSize("large");
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
  };

  // 滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // 使用流式更新 hook
  const { streamTravelData } = useStreamTravelData({
    setMessages,
    scrollToBottom,
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // 驗證目的地
    if (destination.trim() === "") {
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: destination.trim(),
      timestamp: new Date(),
    };

    // 添加用户消息到列表
    setMessages((prev) => [...prev, userMessage]);
    const userInput = destination.trim();
    setDestination(""); // 清空输入框
    setIsLoading(true); // 開始加載

    try {
      // 準備請求數據
      const requestData = {
        destination: userInput,
      };

      // 發送API請求
      const response = await fetch(
        "https://exploreasiait-h5-backend-750219299255.asia-northeast1.run.app/api/ai-planner",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) {
        throw new Error("API request failed.");
      }

      const data = await response.json();

      // 创建消息ID
      const messageId = (Date.now() + 1).toString();

      // 生成基础文本内容（使用JSX）
      const aiResponse = (
        <AIResponseContent
          destination={data.destination}
          userInput={userInput}
        />
      );

      // 先创建消息占位符
      const assistantMessage: Message = {
        id: messageId,
        role: "assistant",
        content: aiResponse,
        timestamp: new Date(),
        data: {
          destination: data.destination,
          itinerary: [],
          topAttractions: [],
          travelTips: [],
        },
      };

      // 添加AI回复到列表
      setMessages((prev) => [...prev, assistantMessage]);

      // 消息已创建，停止加载状态
      setIsLoading(false);

      // 流式更新数据
      const travelData: TravelData = {
        destination: data.destination,
        itinerary: data.itinerary || [],
        topAttractions: data.topAttractions || [],
        travelTips: data.travelTips || [],
      };

      // 模拟流式渲染：逐步添加数据（在后台进行）
      streamTravelData(messageId, travelData).catch((error) => {
        console.error("Stream travel data error:", error);
      });
    } catch (error) {
      console.error("API request error:", error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content:
          "Sorry, I encountered an error processing your request. Please try again later.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false); // 結束加載
    }
  };

  return (
    <>
      <div
        onClick={showLargeDrawer}
        className="hidden md:block bg-[#406388] rounded-full fixed top-1/2 -translate-y-1/2 -right-[60px] z-50 -rotate-90 transform origin-center"
      >
        <button className=" px-4 py-2 flex items-center gap-x-2">
          <div className="rounded-full bg-white flex items-center justify-center">
            <BStarIcon />
          </div>
          <b className="text-white font-extrabold"> AI Travel Planner</b>
        </button>
      </div>

      <div
        onClick={showDefaultDrawer}
        className="block md:hidden bg-[#406388] fixed bottom-6 right-4 z-50 -rotate-90 transform origin-center p-2 rounded-full rounded-bl-none"
      >
        <button className="rounded-full bg-white flex items-center justify-center scale-90">
          <BStarIcon />
        </button>
      </div>

      <Drawer
        title="AI Travel Planner"
        placement="right"
        size={size}
        onClose={onClose}
        open={open}
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
      >
        {/* 消息列表区域  scrollbar-hide */}
        <div className="flex-1 overflow-y-auto px-6 py-6 pb-24">
          <div className="flex flex-col gap-6 w-full">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex w-full ${
                  message.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`rounded-xl sm:px-7 sm:py-4 px-5 py-3 sm:text-lg text-sm leading-relaxed font-semibold ${
                    message.role === "user"
                      ? "bg-[#406388] text-white"
                      : "bg-[#f5f5f5] text-gray-800"
                  }`}
                >
                  {typeof message.content === "string" ? (
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  ) : (
                    message.content
                  )}
                  {message.data && message.role === "assistant" && (
                    <TravelDataRenderer data={message.data} />
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-[#f5f5f5] rounded-xl sm:px-7 sm:py-4 px-5 py-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0s" }}
                    ></div>
                    <div
                      className="w-3 h-3 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    ></div>
                    <div
                      className="w-3 h-3 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.4s" }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* 输入框区域 */}
        <div className="absolute bottom-0 left-0 w-full h-[78px] border-t-[1px] border-[#dee2e6] bg-white z-10 input-search-box">
          <form
            autoComplete="off"
            onSubmit={handleSubmit}
            className="w-full h-full flex items-center justify-center px-6"
          >
            <input
              id="destination-input"
              type="text"
              value={destination}
              onChange={(e) => {
                const value = e.target.value;
                setDestination(value);
              }}
              placeholder="Tell me where you plan to go!"
              className="w-full h-full placeholder:text-[#898989] outline-none ms:text-2xl text-xl font-semibold"
            />

            <button
              type="submit"
              disabled={isLoading || !destination.trim()}
              className={`md:w-14 md:h-14 w-12 h-12 bg-[#406388] rounded-full flex items-center justify-center flex-shrink-0 relative ${
                isLoading || !destination.trim()
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              {isLoading ? (
                <>
                  <span className="scale-125 flex items-center justify-center opacity-30">
                    <SendIcon />
                  </span>
                  {/* <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin scale-125"></div>
                  </div> */}
                </>
              ) : (
                <span className="scale-125 flex items-center justify-center">
                  <SendIcon />
                </span>
              )}
            </button>
          </form>
        </div>
      </Drawer>
    </>
  );
};

export default DrawerBar;
